/*
 * Copyright 2026 Sony Corporation
 */

import type { CoverageOptions } from './coverage';

export type CoverageMode = 'detail' | 'overlap';

export type Rgb = readonly [number, number, number];

/** Nobody sees it. Dark and cold, so it reads as a hole rather than as a bad score. */
export const UNCOVERED: Rgb = [0.22, 0.22, 0.3];
const GOOD: Rgb = [0.13, 0.78, 0.35];
const EDGE: Rgb = [0.98, 0.83, 0.15];
const BAD: Rgb = [0.9, 0.16, 0.12];

const mix = (a: Rgb, b: Rgb, t: number): Rgb => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];

const clamp01 = (value: number): number => Math.min(Math.max(value, 0), 1);

/**
 * Green at half the target or finer, yellow at the target, red at twice it or
 * worse. Logarithmic, because halving a distance halves mm/px and a user thinks
 * of that as one step, not as a fixed number of millimetres.
 */
export const detailColour = (mmPerPx: number, target: number): Rgb => {
  if (!Number.isFinite(mmPerPx)) {
    return UNCOVERED;
  }
  const stops = Math.log2(mmPerPx / target);
  return stops <= 0 ? mix(GOOD, EDGE, clamp01(stops + 1)) : mix(EDGE, BAD, clamp01(stops));
};

/** Red for one camera short of the target, green once it is met; the hole colour for none. */
export const overlapColour = (cameras: number, minOverlap: number): Rgb => {
  if (cameras === 0) {
    return UNCOVERED;
  }
  if (cameras >= minOverlap) {
    return GOOD;
  }
  return minOverlap <= 1 ? GOOD : mix(BAD, EDGE, (cameras - 1) / Math.max(minOverlap - 2, 1));
};

export const colourOf = (
  mode: CoverageMode,
  mmPerPx: number,
  cameras: number,
  options: Pick<CoverageOptions, 'targetMmPerPx' | 'minOverlap'>
): Rgb => (mode === 'detail' ? detailColour(mmPerPx, options.targetMmPerPx) : overlapColour(cameras, options.minOverlap));

export const cssOf = ([r, g, b]: Rgb): string =>
  `rgb(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)})`;
